import React, { useMemo, useCallback, useState } from 'react';
import Select, { components } from 'react-select';
import useLocalization from '../../hooks/useLocalization';

const CustomMultiSelect = ({
  items = [],
  selectedItems = [],
  onChange,
  includeSelectAll = false,
  maxToShow = 10,
  placeholder = '',
  height = '30px',
  showMessageOnlyOnOverflow = false,
  maxItemCustomMessage = length => `+ ${length} user${length === 1 ? '' : 's'} selected`,
  selectAllLabel = 'All Items',
  isMulti = true,
  isGroupped = false,
  closeMenuOnSelect = false,
  closeMenuOnScroll = true,
  fieldColors,
  isClearable = false,
  isDisabled = false,
  containerWidth = 'auto',
  optionRenderer = null,
  placement = 'auto',
  maxHeight,
  customSelectAll = false,
  showInputOnNewLine = false,
  noOptionsMessage
}) => {
  const { translate } = useLocalization();
  const [inputValue, setInputValue] = useState('');
  const selectAllOption = useMemo(() => ({ label: selectAllLabel, value: 'all' }), [selectAllLabel]);

  const handleInputChange = useCallback((newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(newValue);
    }
  }, []);

  const handleBlur = () => {
    const selectedOption =
      !isMulti &&
      inputValue &&
      items?.find(
        i =>
          i?.value === inputValue ||
          i.id === inputValue ||
          (!isNaN(i?.value) && !isNaN(inputValue) && Number(i?.value) === Number(inputValue)) ||
          Number(i?.id) === Number(inputValue)
      );
    if (selectedOption) {
      onChange(selectedOption?.value || selectedOption?.id);
    }

    setInputValue('');
  };

  const options = useMemo(() => {
    const baseOptions =
      isMulti && !isGroupped
        ? items.map(item => ({
          ...item,
          label: item?.label || item?.name,
          value: item.value ?? item._id
        }))
        : items;

    if (includeSelectAll) {
      return [selectAllOption, ...baseOptions];
    }
    return baseOptions;
  }, [items, includeSelectAll, isMulti, isGroupped, selectAllOption]);

  const handleChange = useCallback(
    selectedOptions => {
      if (customSelectAll) {
        const newSelectedValues = selectedOptions.map(option => option.value || option.id);
        onChange(newSelectedValues);
      } else if (isMulti && selectedOptions.some(option => option.value === 'all')) {
        onChange(items.map(item => item._id || item?.value));
      } else {
        onChange(isMulti ? selectedOptions.map(option => option.value) : selectedOptions?.value);
      }
    },
    [onChange, isMulti, items, customSelectAll]
  );

  const value = useMemo(() => {
    if (!isMulti && isGroupped) {
      return items.flatMap(item => item?.options).filter(item => item?.value === selectedItems);
    }
    if (isMulti && isGroupped) {
      return items.flatMap(item => item?.options).filter(item => selectedItems.includes(item?.value));
    }
    if (includeSelectAll && selectedItems.length === items.length) {
      return [selectAllOption];
    }
    if (customSelectAll && selectedItems.length === items.length) {
      return [
        ...items?.map(item => ({
          label: item?.label || item?.name,
          value: item?.value ?? item?._id
        }))
      ];
    }
    return items
      ?.filter(item => selectedItems.includes(item?.value ?? item?._id))
      ?.map(item => ({
        label: item?.label || item?.name,
        value: item?.value ?? item?._id
      }));
  }, [items, selectedItems, isMulti, isGroupped, includeSelectAll, customSelectAll, selectAllOption]);

  const handleSelectAll = useCallback(
    e => {
      e.stopPropagation();
      const isAllSelected = selectedItems?.length === items?.length && items.length > 0;
      if (isAllSelected) {
        onChange([]);
      } else {
        onChange(items.map(item => item.value ?? item._id));
      }
    },
    [selectedItems, items, onChange]
  );

  const MoreSelectedBadge = ({ items }) => {
    const title = items.join(', ');
    const length = items.length;
    const label = maxItemCustomMessage(length);

    return (
      <div className="smallFont fw-bold text-dark" style={{ margin: '1px 1px', fontSize: '12px' }} title={title}>
        {label}
      </div>
    );
  };

  const MultiValue = ({ index, getValue, ...props }) => {
    const overflow = getValue()
      .slice(maxToShow)
      .map(x => x.label);

    return index < maxToShow ? (
      <components.MultiValue {...props} />
    ) : index === maxToShow ? (
      <MoreSelectedBadge items={showMessageOnlyOnOverflow ? getValue() : overflow} />
    ) : null;
  };

  // Custom Option component that uses the optionRenderer
  const Option = props => {
    if (optionRenderer && typeof optionRenderer === 'function') {
      return <components.Option {...props}>{optionRenderer(props.data)}</components.Option>;
    }

    if (!customSelectAll) {
      return <components.Option {...props} />;
    }

    // Handle checkbox click for individual options
    const handleOptionCheckboxClick = e => {
      e.stopPropagation();
      const optionValue = props.data.value;

      // Use the current value state instead of selectedItems prop
      const currentValue = value || [];
      const currentSelectedValues = currentValue.map(item => item.value);

      if (currentSelectedValues.includes(optionValue)) {
        // Remove the option
        const newSelectedValues = currentSelectedValues.filter(val => val !== optionValue);
        onChange(newSelectedValues);
      } else {
        // Add the option
        const newSelectedValues = [...currentSelectedValues, optionValue];
        onChange(newSelectedValues);
      }
    };

    // return checkbox options for customSelectAll
    return (
      <div className="d-flex align-items-center">
        <components.Option {...props} className="d-flex align-items-center">
          <input type="checkbox" checked={props.isSelected} onChange={handleOptionCheckboxClick} />
          <label className="ms-2">{props.label} </label>
        </components.Option>
      </div>
    );
  };

  const CustomMenuList = useCallback(
    props => {
      const isAllSelected = selectedItems?.length === items?.length && items.length > 0;

      return (
        <div>
          {customSelectAll && (
            <>
              <div
                className="select-all d-flex align-items-center gap-2 px-3 py-2"
                onClick={handleSelectAll}
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  onClick={e => e.stopPropagation()} // Prevent double firing
                />
                <label style={{ cursor: 'pointer' }}>{translate('select_all')}</label>
              </div>
              <hr className="m-0" />
            </>
          )}
          <components.MenuList {...props}>{props.children}</components.MenuList>
        </div>
      );
    },
    [customSelectAll, handleSelectAll, selectedItems, items, translate]
  );

  const Input = useCallback(
    props => {
      const hasValues = value && value.length > 0;
      return (
        <div
          style={{
            width: '100%',
            borderTop: hasValues ? '1px solid #ced9d9' : 'none',
            paddingTop: hasValues ? '5px' : '0px',
            marginTop: hasValues ? '5px' : '0px'
          }}
        >
          <components.Input {...props} />
        </div>
      );
    },
    [showInputOnNewLine, value]
  );

  const ValueContainer = useCallback(
    ({ children, ...props }) => <components.ValueContainer {...props}>{children}</components.ValueContainer>,
    []
  );

  const selectComponents = {
    MultiValue,
    Option,
    ...(customSelectAll && {
      MenuList: CustomMenuList
    }),
    ...(showInputOnNewLine && {
      ValueContainer: ValueContainer,
      Input: Input
    })
  };

  return (
    <Select
      isDisabled={isDisabled}
      components={selectComponents}
      styles={{
        container: (provided, state) => ({
          ...provided,
          width: containerWidth
        }),
        control: (provided, state) => ({
          ...provided,
          background: state.isDisabled ? '#efefef' : (fieldColors?.backgroundColor || '#fff'),
          borderColor: '#ced9d9',
          minHeight: height,
          fontSize: 12
        }),
        menuList: (provided, state) => ({
          ...provided,
          fontSize: '12px',
          maxHeight: maxHeight || '200px',
          overflowY: 'auto',
          textWrap: 'wrap',
          zIndex: 9999999999
        }),
        valueContainer: (provided, state) => ({
          ...provided,
          minHeight: height,
          padding: '0px 5px',
          marginTop: '0px',
          ...(showInputOnNewLine && {
            display: 'flex',
            gap: '2px',
            alignItems: 'start'
          })
        }),
        input: (provided, state) => ({
          ...provided,
          margin: '0px',
          padding: '0px',
          ...(showInputOnNewLine && {
            width: '100%',
            minHeight: '30px'
          })
        }),
        indicatorSeparator: state => ({
          display: 'none'
        }),
        indicatorsContainer: (provided, state) => ({
          ...provided,
          maxHeight: 20,
          margin: 'auto 0px',
          padding: '0px'
        }),
        dropdownIndicator: (provided, state) => ({
          ...provided,
          width: 'fit-content',
          padding: 0
        }),
        multiValue: (provided, state) => ({
          ...provided,
          fontSize: '12px',
          height: '20px'
        }),
        placeholder: (provided, state) => ({
          ...provided,
          fontSize: '12px',
          margin: '1px 1px'
        }),
        menuPortal: (provided, state) => ({
          ...provided,
          zIndex: 9999999999,
          minWidth: '80px'
        }),
        option: (provided, { data, isDisabled, isFocused, isSelected }) => {
          let { backgroundColor, textColor, hoverColor, selectedBgColor, selectedColor } = data;
          if (customSelectAll) {
            selectedBgColor = 'white';
            selectedColor = 'dark';
          }
          return {
            ...provided,
            borderBottom: isGroupped && hoverColor ? `1px solid ${hoverColor}` || 'gray' : undefined,
            backgroundColor:
              isGroupped && isSelected
                ? hoverColor || '#d89e31'
                : isGroupped
                  ? backgroundColor || '#fff'
                  : isSelected
                    ? selectedBgColor || '#d89e31'
                    : undefined,
            color: isGroupped ? textColor || 'black' : isSelected ? selectedColor || textColor || 'white' : undefined,
            fontSize: 12,
            ':hover': {
              backgroundColor: isFocused ? hoverColor || '#d89e31' : undefined,
              color: isFocused ? 'white' : undefined
            }
          };
        },
        singleValue: base => ({
          ...base,
          color: fieldColors?.textColor || 'black'
        }),
        groupHeading: base => ({
          ...base,
          fontWeight: 600,
          fontSize: '12px'
        }),
        group: base => ({
          ...base,
          paddingTop: 0,
          background: 'rgb(237, 237, 237)'
        })
      }}
      isMulti={isMulti}
      options={options}
      value={value}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      onBlur={handleBlur}
      isClearable={isClearable}
      placeholder={placeholder}
      className="basic-multi-select"
      classNamePrefix="select"
      menuPortalTarget={document.body}
      onChange={handleChange}
      hideSelectedOptions={false}
      closeMenuOnSelect={closeMenuOnSelect}
      closeMenuOnScroll={closeMenuOnScroll}
      menuPlacement={placement}
      isSearchable={true}
      noOptionsMessage={() => noOptionsMessage || translate('no_options')}
    />
  );
};

export default CustomMultiSelect;
